
import React from 'react'
import { Box, Paper, TableContainer, Table, TableBody, TableHead, TableRow, TableCell, tableCellClasses, CircularProgress } from '@mui/material'
import { styled } from '@mui/material/styles'
import Pagination from '../../../../components/TablePagination'
import { fetchBubbleKpiHistoryTable } from '../../redux/actionCreators'
import { type HistoryBubbleTableState } from '../../redux/HistoryBubbleTableSlice'
import { type RootState } from '../../../../store'
import { type ConnectedProps, connect, useSelector } from 'react-redux'
import { type ToggleOption } from '../../../../components/GenericToggleButton'
import dayjs from 'dayjs'
import { getColumnsByKpi } from './HistoryTableCustomColumns'
import { type BubbleKpiState, type HistoryBubbleTableData } from '../../types'
import { getBubbleTypeCode, getDetailKeyword } from './kpiUtils'
import { type KpiKeyBubble } from '../../redux/changeKpiType'
import { convertDateIntoAohStandardUsingDayjs } from '../../../../customHooks/parseDate'
import AuthError from '../../../../components/ErrorComponents/AuthError'

interface Sort {
  order: Order
  orderBy: string
}

interface ParentProps {
  selectedBar: ToggleOption
  selectedDate: dayjs.Dayjs
  selectedBubble: number
  rowPerPage: number
  page: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  sort: Sort
  setSort: React.Dispatch<React.SetStateAction<Sort>>
}
export type Order = 'asc' | 'desc'

interface HeadCell {
  disablePadding: boolean
  id: any
  label: string
}

function EnhancedTableHead (props: any): any {
  const { headCells } = props
  return (
    <TableHead data-testid="table-header">
      <TableRow>
        {headCells.map((headCell: HeadCell) => (
          <StyledTableCell
            sx={{ whiteSpace: 'nowrap' }}
            key={headCell.id}
            align={'left'}
            padding={'normal'}
          >
            {headCell.label}
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '& th': {
    border: '1px solid #202020'
  }
}))
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: '#F0F0F0',
    fontWeight: '600',
    fontSize: 12,
    border: '2px solid #202020',
    padding: '5px 16px',
    fontFamily: 'Honeywell Sans Web',
    lineHeight: '20px',
    textTransform: 'uppercase'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: '#d0d0d0',
    paddingLeft: '17px',
    borderBottom: '1px solid #202020',
    borderRight: 0,
    fontFamily: 'Honeywell Sans Web',
    whiteSpace: 'nowrap'
  }
}))
function HistoryBubbleKpiTableComponent (props: AllProps): JSX.Element {
  const [assets, setAssets] = React.useState<HistoryBubbleTableData[]>([])
  const selectedKpi = useSelector((state: RootState) => state.kpiType)
  const [detailKeyword, detailService] = getDetailKeyword(selectedKpi, props.selectedBar.value)
  const headCells = getColumnsByKpi(detailKeyword)

  const fetchAssets = (): void => {
    if (props.page >= 1) {
      props.fetchBubbleKpiHistoryTable(detailKeyword, dayjs(props.selectedDate).format('YYYY-MM-DD'), getBubbleTypeCode(selectedKpi as KpiKeyBubble, props.selectedBubble, props.spreadFactor), props.page - 1, props.rowPerPage, detailService)
    }
  }

  React.useEffect(() => {
    if (props.historyBubbleTableState.data !== undefined) {
      setAssets(props.historyBubbleTableState.data.assetDetails)
    }
  }, [props.historyBubbleTableState])

  if (props.historyBubbleTableState.data !== undefined && props.historyBubbleTableState.httpStatus === 200 && (props.historyBubbleTableState.data.assetDetails !== undefined && props.historyBubbleTableState.data.assetDetails.length > 0)) {
    return (
      <Box className="devices" sx={{ overflowX: 'auto' }}>
        <Paper sx={{ width: '100%', mb: 2, backgroundColor: '#272727', boxShadow: 'unset', color: '#F0F0F0', marginBottom: '0' }}>
          <TableContainer className="device-table">
            <Table
              sx={{ minWidth: 750, color: '#f0f0f0' }}
              aria-labelledby="tableTitle"
              size={'small'}
            >
              <EnhancedTableHead
                headCells={headCells}
              />
              <TableBody>
                {assets.map((row, index) => {
                  return (
                      <StyledTableRow
                        hover
                        tabIndex={-1}
                        key={index}
                        aria-label="asset-list-row"
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {
                          headCells.map((cell, idx) => {
                            return (
                              <StyledTableCell data-testid={`row-${index}-col-${idx}`} key={cell.id} sx={{ padding: '1.2px 16px', color: '#64C3FF' }}>
                                {cell.isDate ? convertDateIntoAohStandardUsingDayjs(row[cell.id as keyof typeof row]) : row[cell.id as keyof typeof row]}
                              </StyledTableCell>
                            )
                          })
                        }
                      </StyledTableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {props.historyBubbleTableState.data !== undefined && props.historyBubbleTableState.data.totalCount > 10 && (<Pagination
            count={props.historyBubbleTableState.data.totalCount}
            page={props.page}
            setPage={props.setPage}
            rowsPerPage={props.rowPerPage}
            siblingCount={1}
            boundaryCount={1}
          />)}
        </Paper>
      </Box >
    )
  } else if (props.historyBubbleTableState.isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }} className="CircularprogressAmi">
        <CircularProgress />
      </div>
    )
  } else if (props.historyBubbleTableState.data !== undefined && props.historyBubbleTableState.httpStatus === 200 && props.historyBubbleTableState.data.assetDetails.length === 0) {
    return (
      <div className="authError">
        <AuthError errorMessage="NoDataPresent" />
      </div>
    )
  } else if (props.historyBubbleTableState.httpStatus === 401) {
    return (
      <div className="authError">
        <AuthError errorMessage="Unauthorized" />
      </div>
    )
  } else if (props.historyBubbleTableState.httpStatus === 403) {
    return (
      <div className="authError">
        <AuthError errorMessage="accessForbidden" />
      </div>
    )
  } else {
    return (
      <div className="authError" style={{ margin: '1em' }}>
        <AuthError errorMessage="cannotFetch" retry={fetchAssets} />
      </div>
    )
  }
}

interface DispatchToProps {
  fetchBubbleKpiHistoryTable: (
    kpiType: string,
    inputDateRead: string,
    donutType: string,
    page: number,
    size: number, service: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchBubbleKpiHistoryTable: (
    kpiType: string,
    inputDateRead: string,
    donutType: string,
    page: number,
    size: number, service: string) => dispatch(fetchBubbleKpiHistoryTable(kpiType, inputDateRead, donutType, page, size, service))
})

interface StateToProps {
  historyBubbleTableState: HistoryBubbleTableState
  spreadFactor: BubbleKpiState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  historyBubbleTableState: state.KpiHistoryBubbleState,
  spreadFactor: state.SpreadFactorState
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>
type AllProps = ParentProps & PropsFromRedux

export default connector(HistoryBubbleKpiTableComponent)
